import { AnimatePresence, motion } from 'framer-motion';
import React, { FC } from 'react';
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { useLocale } from '../../../../hooks/index';
import { activeColors, fillColors, strokeColors } from '../../../cap-table/graphs/index';
import { GraphVariant } from '../../variables';

type EmptyGraphProps = {
  isLoadingFinished: boolean;
  variant: GraphVariant;
};

export const EmptyGraph: FC<EmptyGraphProps> = ({ variant, isLoadingFinished }) => {
  const { messagesLocale } = useLocale();

  const data = [
    { date: '', totalShares: 0, percentage: 0 },
    { date: '', totalShares: 4, percentage: 20 },
    { date: '', totalShares: 3, percentage: 15 },
    { date: `${messagesLocale['FULLY_DILUTED']}`, totalShares: 8, percentage: 40 },
  ];

  const stakeholderColors = data.reduce(
    (acc, _, index) => {
      acc[index] = {
        fill: fillColors[index % fillColors.length],
        index,
        stroke: strokeColors[index % strokeColors.length],
        active: activeColors[index % activeColors.length],
      };
      return acc;
    },
    {} as Record<string, { active: string; fill: string; index: number; stroke: string }>,
  );

  const yAxisDomain = variant === GraphVariant.OWNERSHIP_PERCENT ? [0, 100] : [0, 20];

  return (
    <div className="centered relative flex h-full w-full pt-5">
      <AnimatePresence>
        {isLoadingFinished && (
          <motion.span
            animate={{ opacity: 1, y: 0 }}
            className="absolute z-1 text-label-md font-medium text-gray-500"
            exit={{ opacity: 0, y: -6 }}
            initial={{ opacity: 0, y: 6 }}
            key="valuation-empty-message"
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <AppFormattedMessage id="VALUATION_SNAPSHOT_WILL_BE_DISPLAYED_HERE" />
          </motion.span>
        )}
      </AnimatePresence>
      <ResponsiveContainer height="100%" width="100%">
        <AreaChart
          data={data}
          margin={{
            top: 0,
            right: 0,
            left: 8,
            bottom: 8,
          }}
        >
          <XAxis
            dataKey="date"
            stroke="#475467"
            tick={{ fontSize: 10, fontWeight: 500 }}
            tickLine={false}
            tickMargin={16}
          />
          <YAxis
            domain={yAxisDomain}
            stroke="#475467"
            tick={{ fontSize: 10, fontWeight: 500 }}
            tickFormatter={(value) =>
              variant === GraphVariant.OWNERSHIP_PERCENT ? `${value}%` : value
            }
            tickLine={false}
            tickMargin={16}
          />
          {data.map((_, index) => (
            <Area
              activeDot={{
                strokeWidth: 1,
                fill: stakeholderColors[index].active,
              }}
              className="blur-md"
              dataKey={'percentage'}
              fill={stakeholderColors[index].fill}
              fillOpacity={1}
              key={index}
              stackId="1"
              stroke={stakeholderColors[index].stroke}
              type="monotone"
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
